import { useMemo, useState } from 'react';
import { useQzPrint } from 'src/hooks/useQzPrint';
import { useQzTray } from 'src/hooks/useQzTray';
import { LabelPrintData } from 'src/apis/labelApi';
import { blueButtonClass, grayButtonClass } from './common';

interface LabelPrintButtonProps {
	lotNo?: string;
	labelDataList?: LabelPrintData[];
	/** 외부 출력 로직 주입 (설비 라벨 등). 있으면 내부 로직 무시. */
	onPrint?: () => Promise<void>;
	disabled?: boolean;
	className?: string;
	buttonText?: string;
}

export const LabelPrintButton = ({
	lotNo,
	labelDataList,
	onPrint,
	disabled = false,
	className = '',
	buttonText = '라벨 출력'
}: LabelPrintButtonProps) => {
	const { isConnected } = useQzTray();
	const { printLabel, printBulkLabel } = useQzPrint();
	const [isPrinting, setIsPrinting] = useState(false);

	const handlePrint = async () => {
		if (disabled || isPrinting) return;
		setIsPrinting(true);
		try {
			if (onPrint) {
				await onPrint();
			} else if (labelDataList && labelDataList.length > 0) {
				await printBulkLabel(labelDataList, 'label1');
			} else if (lotNo) {
				await printLabel(lotNo, 'label1');
			}
		} finally {
			setIsPrinting(false);
		}
	};

	const isDisabled = useMemo(() => {
		if (disabled || isPrinting) return true;
		if (onPrint) return false;
		if (labelDataList && labelDataList.length === 0) return true;
		if (!lotNo && (!labelDataList || labelDataList.length === 0)) return true;
		return false;
	}, [disabled, isPrinting, lotNo, labelDataList, onPrint]);

	const displayText = useMemo(() => {
		if (!isConnected) return `${buttonText} (QZ 미연결)`;
		if (isPrinting) return '출력 중...';
		if (labelDataList && labelDataList.length > 1) return `${buttonText} (${labelDataList.length})`;
		return buttonText;
	}, [isPrinting, isConnected, labelDataList, buttonText]);

	return (
		<button
			onClick={handlePrint}
			disabled={isDisabled}
			className={`h-full text-white rounded-[4px] px-[16px] mb-[6px] font-semibold inline-flex items-center gap-2 ${className} ${
				isDisabled ? grayButtonClass : blueButtonClass
			}`}
			title={
				!isConnected ? '브라우저 인쇄로 출력합니다 (QZ 미연결)' :
				isDisabled ? '출력할 설비를 선택하세요' : '라벨을 출력합니다'
			}
		>
			<svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4"
				fill="none" viewBox="0 0 24 24" stroke="currentColor">
				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
					d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
			</svg>
			<span>{displayText}</span>
			{isPrinting && <span className="inline-block animate-spin">⟳</span>}
		</button>
	);
};

export default LabelPrintButton;
